import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from '@hotjar/hotsauce';
import { mapLabelsToMultiSelectOptions, useAvailableLabels, useCreateLabel } from '@hotjar/labels';
import type { LabelModel } from '@hotjar/labels';

import type { Highlight } from '@hotjar/webapp/domains/highlights/model';
import type { MultiSelectProps } from '@hotjar/hotsauce';
import { useHighlightLabels } from './use-highlight-labels';

const StyledMultiSelect = styled(MultiSelect)`
  z-index: 2;
  div[data-tippy-root] {
    position: relative;
    z-index: 2;
    :hover {
      position: relative;
      z-index: 2;
    }
  }
`;

const mapLabelsToMultiSelectOptionsWithTranslations = (
  t: (key: string) => string,
  labels?: Array<LabelModel>,
): MultiSelectOptions => {
  return mapLabelsToMultiSelectOptions(
    labels?.map((label) => ({ ...label, name: label.keyName ? t(label.keyName) : label.name })),
  );
};

type MultiSelectOptions = MultiSelectProps['availableOptions'];

interface LabelsMultiSelectProps {
  itemId: Highlight['id'];
  itemLabels: Highlight['labels'];
  siteId: string | null;
}

export function LabelsMultiSelect({
  itemId,
  itemLabels,
  siteId,
}: LabelsMultiSelectProps): JSX.Element | null {
  const { t } = useTranslation();
  const { add: addLabel, remove: removeLabel } = useHighlightLabels(siteId, String(itemId));

  const { createLabel } = useCreateLabel(Number(siteId));
  const { data: labels } = useAvailableLabels(siteId, 'all');
  const [availableLabels, setAvailableLabels] = useState<MultiSelectOptions>(
    mapLabelsToMultiSelectOptionsWithTranslations(t, labels),
  );

  useEffect(() => {
    setAvailableLabels(mapLabelsToMultiSelectOptionsWithTranslations(t, labels));
  }, [labels, setAvailableLabels, t]);

  const [selectedLabels, setSelectedLabels] = useState<MultiSelectOptions>(
    mapLabelsToMultiSelectOptionsWithTranslations(t, itemLabels),
  );

  useEffect(() => {
    setSelectedLabels(mapLabelsToMultiSelectOptionsWithTranslations(t, itemLabels));
  }, [setSelectedLabels, itemLabels, t]);

  const onLabelsChange = (selectedOptions: MultiSelectOptions) => {
    const addedLabels = selectedOptions?.filter(
      (option) => !selectedLabels?.find((label) => label.value === option.value),
    );
    const removedLabels = selectedLabels?.filter(
      (label) => !selectedOptions?.find((option) => label.value === option.value),
    );

    addedLabels?.forEach((label) => addLabel(label.value));
    removedLabels?.forEach((label) => removeLabel(label.value));

    setSelectedLabels(selectedOptions);
  };

  const onAddCustomLabel = async (name: string) => {
    const newLabel = await createLabel(name.charAt(0).toUpperCase() + name.slice(1));

    if (newLabel) {
      setSelectedLabels([
        ...(selectedLabels ?? []),
        { value: String(newLabel.id), label: newLabel.name },
      ]);
      addLabel(newLabel.id);

      onLabelsChange([
        ...(selectedLabels ?? []),
        { value: String(newLabel.id), label: newLabel.name },
      ]);
    }
  };

  return (
    <StyledMultiSelect
      size="s"
      availableOptions={availableLabels}
      selectedOptions={selectedLabels}
      placeholder={t('highlights:popover.add-label')}
      onChange={onLabelsChange}
      hasAddOption
      onAddOption={(name) => onAddCustomLabel(name)}
    />
  );
}
